import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import Hls from "hls.js";
import { apiFetch } from "@/lib/api-client";
import { Skeleton } from "@/components/skeleton";
import { WebinarSurveyPanel } from "@/components/webinar/webinar-survey-panel";

type ReplayTab = "chat" | "polls" | "cta" | "reactions";

interface ReplayChatEvent {
  id: string;
  senderName: string;
  senderRole: string;
  content: string;
  timestampSeconds: number;
}

interface ReplayPollEvent {
  id: string;
  question: string;
  pollType: string;
  status: string;
  launchedAtSeconds: number | null;
  closedAtSeconds: number | null;
  options: Array<{ id: string; label: string; voteCount: number; percentage: number }>;
  totalResponses: number;
}

interface ReplayCtaEvent {
  id: string;
  label: string;
  url: string | null;
  ctaType: string;
  displayAtSeconds: number;
  durationSeconds: number;
  clickCount: number;
}

interface ReplayReactionEvent {
  id: string;
  timestampSeconds: number;
  reactionCounts: Record<string, number>;
}

interface ReplayMarker {
  id: string;
  type: string;
  label: string;
  timestampSeconds: number;
}

interface ReplayTimelinePayload {
  recording: { id: string; durationSeconds: number };
  timeline: {
    chat: ReplayChatEvent[];
    polls: ReplayPollEvent[];
    ctas: ReplayCtaEvent[];
    reactions: ReplayReactionEvent[];
  };
  markers: ReplayMarker[];
}

/**
 * G.8: Webinar Replay Page
 * HLS.js player with:
 * - Playback speed controls (0.5x-2x)
 * - Resume where left off (localStorage)
 * - Chat replay at timestamps (connected in Layer 1.1)
 * - Access control (registration required — checked by API)
 */
export default function ReplayPage() {
  const { webinarId } = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [replayUrl, setReplayUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [timeline, setTimeline] = useState<ReplayTimelinePayload | null>(null);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [activeTab, setActiveTab] = useState<ReplayTab>("chat");

  // Fetch replay URL
  useEffect(() => {
    if (!webinarId) return;
    async function fetchReplay() {
      try {
        const [recording, timelinePayload] = await Promise.all([
          apiFetch<{ replayUrl: string; durationSeconds: number | null }>(
            `/v1/webinars/${webinarId}/replay-url`,
          ),
          apiFetch<{ data: ReplayTimelinePayload }>(
            `/v1/webinars/${webinarId}/replay/timeline`,
          ),
        ]);
        setReplayUrl(recording.replayUrl);
        setDurationSeconds(recording.durationSeconds ?? timelinePayload.data.recording.durationSeconds);
        setTimeline(timelinePayload.data);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Replay not available");
        setIsLoading(false);
      }
    }
    fetchReplay();
  }, [webinarId]);

  // Initialize HLS.js
  useEffect(() => {
    if (!replayUrl || !videoRef.current) return;
    const video = videoRef.current;

    // Resume from localStorage
    const savedTime = localStorage.getItem(`replay-${webinarId}`);
    const startTime = savedTime ? parseFloat(savedTime) : 0;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(replayUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (startTime > 0) video.currentTime = startTime;
        video.play().catch(() => {});
      });
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = replayUrl;
      video.addEventListener("loadedmetadata", () => {
        if (startTime > 0) video.currentTime = startTime;
        video.play().catch(() => {});
      });
    }
  }, [replayUrl, webinarId]);

  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const syncCurrentTime = () => setCurrentTimeSeconds(video.currentTime);
    video.addEventListener("timeupdate", syncCurrentTime);
    video.addEventListener("seeked", syncCurrentTime);
    video.addEventListener("loadedmetadata", syncCurrentTime);
    return () => {
      video.removeEventListener("timeupdate", syncCurrentTime);
      video.removeEventListener("seeked", syncCurrentTime);
      video.removeEventListener("loadedmetadata", syncCurrentTime);
    };
  }, []);

  // Save position periodically
  useEffect(() => {
    if (!videoRef.current || !webinarId) return;
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (video && !video.paused) {
        localStorage.setItem(`replay-${webinarId}`, String(video.currentTime));
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [webinarId]);

  // Playback rate control
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const visibleChat = useMemo(() => (
    timeline?.timeline.chat.filter((message) => message.timestampSeconds <= currentTimeSeconds) ?? []
  ), [currentTimeSeconds, timeline]);

  const visiblePolls = useMemo(() => (
    timeline?.timeline.polls.filter((poll) => (poll.launchedAtSeconds ?? Number.MAX_SAFE_INTEGER) <= currentTimeSeconds) ?? []
  ), [currentTimeSeconds, timeline]);

  const visibleReactions = useMemo(() => (
    timeline?.timeline.reactions.filter((reaction) => reaction.timestampSeconds <= currentTimeSeconds).slice(-8) ?? []
  ), [currentTimeSeconds, timeline]);

  const activeCta = useMemo(() => {
    const ctas = timeline?.timeline.ctas ?? [];
    return ctas.findLast((cta) => {
      if (cta.displayAtSeconds > currentTimeSeconds) {
        return false;
      }
      if (cta.durationSeconds <= 0) {
        return true;
      }
      return currentTimeSeconds <= cta.displayAtSeconds + cta.durationSeconds;
    }) ?? null;
  }, [currentTimeSeconds, timeline]);

  const ctaCountdown = activeCta && activeCta.durationSeconds > 0
    ? Math.max(0, activeCta.durationSeconds - Math.max(0, Math.floor(currentTimeSeconds - activeCta.displayAtSeconds)))
    : null;

  function seekTo(timestampSeconds: number) {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = timestampSeconds;
    setCurrentTimeSeconds(timestampSeconds);
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="space-y-3">
          <Skeleton className="h-6 w-32 bg-white/15" />
          <Skeleton className="h-3 w-48 bg-white/10" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-lg text-white">{error}</p>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">The replay may have expired or is not yet available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-black">
      <div className="grid flex-1 gap-0 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="flex min-h-0 flex-col">
          <div className="relative flex-1 bg-black">
            <video
              ref={videoRef}
              className="h-full w-full object-contain"
              playsInline
              controls
            />
            {activeCta ? (
              <div className="pointer-events-none absolute right-6 bottom-6 z-10 max-w-sm rounded-2xl border border-white/15 bg-black/75 p-4 text-white shadow-2xl backdrop-blur">
                <p className="text-xs uppercase tracking-[0.24em] text-white/60">Replay offer</p>
                <h2 className="mt-2 text-xl font-semibold">{activeCta.label}</h2>
                <p className="mt-2 text-sm text-white/70">
                  {activeCta.clickCount} replay clicks recorded so far.
                </p>
                {ctaCountdown !== null ? (
                  <p className="mt-3 text-sm font-medium text-amber-300">
                    Ends in {Math.floor(ctaCountdown / 60)}m {ctaCountdown % 60}s
                  </p>
                ) : null}
                {activeCta.url ? (
                  <a
                    className="pointer-events-auto mt-4 inline-flex rounded-full bg-white px-4 py-2 text-sm font-medium text-black"
                    href={activeCta.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open offer
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="border-t border-gray-800 bg-gray-900 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--color-muted-foreground)]">Speed:</span>
                {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setPlaybackRate(rate)}
                    className={`stagger-item rounded px-2 py-1 text-xs ${
                      playbackRate === rate
                        ? "bg-primary text-white"
                        : "text-[var(--color-muted-foreground)] hover:text-white"
                    }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
              {durationSeconds !== null ? (
                <span className="text-xs text-[var(--color-muted-foreground)]">
                  {formatTimestamp(currentTimeSeconds)} / {formatTimestamp(durationSeconds)}
                </span>
              ) : null}
            </div>
            {durationSeconds && timeline ? (
              <div className="mt-3">
                <div className="relative h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="absolute inset-y-0 left-0 bg-white/30"
                    style={{ width: `${Math.min(100, (currentTimeSeconds / durationSeconds) * 100)}%` }}
                  />
                  {timeline.markers.map((marker) => (
                    <button
                      key={marker.id}
                      type="button"
                      aria-label={`Jump to ${marker.label}`}
                      className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-300"
                      style={{ left: `${Math.min(100, (marker.timestampSeconds / durationSeconds) * 100)}%` }}
                      onClick={() => seekTo(marker.timestampSeconds)}
                    />
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-white/55">
                  {timeline.markers.slice(0, 4).map((marker) => (
                    <button
                      key={`${marker.id}-chip`}
                      type="button"
                      className="rounded-full border border-white/10 px-2 py-1 hover:border-white/25"
                      onClick={() => seekTo(marker.timestampSeconds)}
                    >
                      {marker.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <aside className="flex min-h-0 flex-col border-l border-gray-800 bg-[#050505]">
          <div className="border-b border-gray-800 px-4 py-3">
            <div className="flex gap-2">
              {([
                ["chat", "Chat"],
                ["polls", "Polls"],
                ["cta", "CTA"],
                ["reactions", "Reactions"],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                    activeTab === key
                      ? "bg-white text-black"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            {activeTab === "chat" ? (
              <div className="space-y-3">
                {visibleChat.length === 0 ? (
                  <p className="text-sm text-white/55">Chat messages will appear as the replay progresses.</p>
                ) : visibleChat.map((message) => (
                  <article key={message.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-white">{message.senderName}</p>
                      <p className="text-xs text-white/45">{formatTimestamp(message.timestampSeconds)}</p>
                    </div>
                    <p className="mt-2 text-sm text-white/75">{message.content}</p>
                  </article>
                ))}
              </div>
            ) : null}

            {activeTab === "polls" ? (
              <div className="space-y-3">
                {visiblePolls.length === 0 ? (
                  <p className="text-sm text-white/55">Polls will appear when they were launched in the live session.</p>
                ) : visiblePolls.map((poll) => {
                  const isClosed = poll.closedAtSeconds !== null && poll.closedAtSeconds <= currentTimeSeconds;
                  return (
                    <article key={poll.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-white">{poll.question}</p>
                        <span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-white/60">
                          {isClosed ? "Closed" : "Live then"}
                        </span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {poll.options.map((option) => (
                          <div key={option.id}>
                            <div className="flex items-center justify-between text-xs text-white/65">
                              <span>{option.label}</span>
                              <span>{option.voteCount} votes · {option.percentage}%</span>
                            </div>
                            <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/10">
                              <div className="h-full rounded-full bg-emerald-400" style={{ width: `${option.percentage}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}

            {activeTab === "cta" ? (
              <div className="space-y-3">
                {(timeline?.timeline.ctas.filter((cta) => cta.displayAtSeconds <= currentTimeSeconds) ?? []).length === 0 ? (
                  <p className="text-sm text-white/55">Offers will appear in sync with the replay timeline.</p>
                ) : (timeline?.timeline.ctas.filter((cta) => cta.displayAtSeconds <= currentTimeSeconds) ?? []).map((cta) => (
                  <article key={cta.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-white">{cta.label}</p>
                      <p className="text-xs text-white/45">{formatTimestamp(cta.displayAtSeconds)}</p>
                    </div>
                    <p className="mt-2 text-sm text-white/65">
                      {cta.clickCount} clicks • {cta.ctaType}
                    </p>
                  </article>
                ))}
              </div>
            ) : null}

            {activeTab === "reactions" ? (
              <div className="space-y-3">
                {visibleReactions.length === 0 ? (
                  <p className="text-sm text-white/55">Reaction bursts will appear as playback crosses their original intervals.</p>
                ) : visibleReactions.map((reaction) => (
                  <article key={reaction.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-white">Audience reactions</p>
                      <p className="text-xs text-white/45">{formatTimestamp(reaction.timestampSeconds)}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-sm text-white/70">
                      {Object.entries(reaction.reactionCounts).map(([name, count]) => (
                        <span key={name} className="rounded-full border border-white/10 px-2 py-1">
                          {name} × {count}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="border-t border-gray-800 bg-black px-4 py-4">
        <div className="mx-auto max-w-4xl">
          <WebinarSurveyPanel
            webinarId={webinarId}
            displayAt="post"
            theme="dark"
            title="Post-webinar survey"
            emptyLabel="No post-webinar survey is active for this replay."
          />
        </div>
      </div>
    </div>
  );
}

function formatTimestamp(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}
